import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useBalanceSSE } from './useSSE';

/**
 * Hook to get user balance with real-time SSE updates.
 *
 * Does one initial GET fetch, then receives live updates via SSE.
 * The refetch() function is still available for manual refresh.
 *
 * @param {boolean} autoFetch - Auto-fetch on mount (default: false)
 * @returns {Object} { balance, loading, error, refetch }
 */
export function useBalance(autoFetch = false) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // SSE subscription for real-time balance updates
  const { balance: sseBalance } = useBalanceSSE();

  const fetchBalance = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Call billingservice endpoint (returns credits-based org wallet balance)
      const response = await axios.get('/api/v1/balance');
      const data = response.data;
      // current_balance / reserved_amount / available_balance are now in credits (100 = £1)
      const creditBalance = parseFloat(data.current_balance || 0);
      const creditReserved = parseFloat(data.reserved_amount || 0);
      const available = parseFloat(data.available_balance || 0);
      setBalance({
        // Credits
        creditBalance,
        creditReserved,
        availableCredits: available,
        // GBP equivalents for display
        current: creditBalance / 100,
        reserved: creditReserved / 100,
        available: available / 100,
        currency: data.currency || 'credits',
      });
      return data;
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      setError(new Error(errorMsg));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) {
      fetchBalance();
    }
  }, [autoFetch, fetchBalance]);

  // Apply SSE balance updates when they arrive
  useEffect(() => {
    if (sseBalance) {
      setBalance(sseBalance);
    }
  }, [sseBalance]);

  return {
    balance,
    loading,
    error,
    refetch: fetchBalance
  };
}
